import { beginWorkspaceOperation } from './workspaceBusy';

export interface ApiEnvelope<T> {
  success: boolean;
  code: string;
  message: string;
  data: T;
}

export interface AdminSession {
  adminId: string;
  username: string;
  displayName: string;
  role: 'PLATFORM_ADMIN' | 'OPERATIONS' | 'DEVELOPER' | 'AUDITOR' | 'REGIONAL_REGULATOR';
  regionId?: string;
  csrfToken?: string;
  csrfSessionId?: string;
  expiresAt?: string;
}

interface SaveFilePickerType {
  description?: string;
  accept: Record<string, string[]>;
}

interface SaveFilePickerOptions {
  suggestedName?: string;
  types?: SaveFilePickerType[];
  excludeAcceptAllOption?: boolean;
}

type SaveFilePickerWindow = Window & typeof globalThis & {
  showSaveFilePicker?: (options?: SaveFilePickerOptions) => Promise<FileSystemFileHandle>;
};

export interface DownloadOptions {
  saveLocation?: SaveLocation;
}

export interface DownloadResult {
  filename: string;
  method: 'file-picker' | 'browser-download';
}

export interface SaveLocation {
  fileHandle: FileSystemFileHandle | null;
  method: 'file-picker' | 'browser-download';
}

export interface SaveLocationOptions {
  suggestedName?: string;
  types?: SaveFilePickerType[];
}

let csrfToken = sessionStorage.getItem('yj_admin_csrf') || '';
let csrfSessionId = sessionStorage.getItem('yj_admin_csrf_session') || '';
let sessionGeneration = 0;
let csrfRefresh: Promise<string> | null = null;

const errorText: Record<string, string> = {
  AUTH_REQUIRED: '登录已过期，请重新登录',
  ROLE_FORBIDDEN: '当前后台账号没有此操作权限',
  REQUEST_INVALID: '提交内容不完整或格式不正确，请检查后重试',
  DEVICE_VOIP_NOT_READY: '话机的机型审核、keyVersion、逐机登记或签名尚未全部就绪，暂不能启用亲情通话',
  WMPF_PLATFORM_NOT_CONFIGURED: '服务端尚未配置 WMPF 移动应用凭据',
  WMPF_PLATFORM_UNAVAILABLE: '微信 WMPF 平台暂时不可用，请稍后重试',
  WMPF_MODEL_NOT_READY: 'WMPF 机型尚未审核通过，或 productId、model_name、keyVersion 不完整',
  WMPF_DEVICE_REGISTRATION_FAILED: '设备未能登记到 WMPF，请查看库存列表中的微信错误说明',
  WMPF_SIGNING_KEY_NOT_CONFIGURED: '服务端尚未配置 WMPF 签名密钥文件',
  WMPF_SIGNING_FAILED: '逐机签名生成或校验失败，请核对 WMPF 公私钥',
  ELECTRONIC_CARD_NUMBER_INVALID: '校牌编号必须原样填写8位数字，不能补零、截断或转换',
  ELECTRONIC_CARD_NUMBER_CONFLICT: '该校牌编号已被其他学生或登记申请使用，请重新核对',
  ELECTRONIC_CARD_NUMBER_LOCKED: '该校牌已制作并用于考勤，不能只修改编号；如需更换必须同时重制全部实体校牌',
  ATTENDANCE_RULE_INVALID: '考勤规则内容不完整或时间顺序不正确，请重新检查',
  ATTENDANCE_RULE_CONFLICT: '同一学校或班级在该生效日期内已有启用中的考勤规则',
  ATTENDANCE_EXPORT_TOO_LARGE: '导出数据量过大，请缩短日期范围或选择具体班级后重试',
  ORDER_STATE_INVALID: '当前订单状态不允许执行此操作',
  IDEMPOTENCY_KEY_CONFLICT: '本次操作内容已经变化，请刷新资料后重新提交',
  DELETION_BLOCKED: '仍有未处理业务或关联记录，暂不能继续删除，请先完成待处理项',
  CONFIG_REVISION_CONFLICT: '该学校配置已被其他管理员更新，请刷新详情后重新操作',
  SCHOOL_POSITIONING_BADGE_DISABLED: '本校暂未开通双向通话定位校牌',
  CSRF_TOKEN_INVALID: '页面安全令牌已失效，请刷新后重试',
  RATE_LIMITED: '操作过于频繁，请稍后重试',
  INTERNAL_ERROR: '服务暂时不可用，请稍后重试'
};

// Only documented business codes may carry backend-approved Chinese publicMessage text.
// Authentication, authorization and internal failures always use our fixed safe messages.
const publicBusinessCodes = new Set([
  'REQUEST_INVALID', 'ORDER_STATE_INVALID', 'DELETION_BLOCKED', 'CONFIG_REVISION_CONFLICT',
  'ELECTRONIC_CARD_NUMBER_INVALID', 'ELECTRONIC_CARD_NUMBER_CONFLICT', 'ELECTRONIC_CARD_NUMBER_LOCKED',
  'ATTENDANCE_RULE_INVALID', 'ATTENDANCE_RULE_CONFLICT', 'ATTENDANCE_EXPORT_TOO_LARGE',
  'SCHOOL_POSITIONING_BADGE_DISABLED'
]);

const expireSession = (message = errorText.AUTH_REQUIRED): never => {
  csrfToken = '';
  csrfSessionId = '';
  csrfRefresh = null;
  sessionGeneration += 1;
  sessionStorage.removeItem('yj_admin_csrf');
  sessionStorage.removeItem('yj_admin_csrf_session');
  window.dispatchEvent(new CustomEvent('admin-session-expired'));
  throw new Error(message);
};

const changedSessionMessage = '后台会话已变更，请刷新页面后重新操作';

const responseError = (response: Response, body: ApiEnvelope<unknown> | null): Error => {
  if (response.status === 401) throw new Error(errorText.AUTH_REQUIRED);
  if (response.status >= 500 || body?.code === 'INTERNAL_ERROR') return new Error(errorText.INTERNAL_ERROR);
  if (response.status === 403) return new Error(body?.code === 'CSRF_TOKEN_INVALID' ? errorText.CSRF_TOKEN_INVALID : errorText.ROLE_FORBIDDEN);
  const message = typeof body?.message === 'string' ? body.message.trim() : '';
  if (body && publicBusinessCodes.has(body.code) && message.length <= 500
      && /[\u3400-\u9fff]/.test(message) && !/[<>\r\n]/.test(message)) {
    return new Error(message);
  }
  return new Error((body?.code && errorText[body.code]) || `请求失败（${response.status}）`);
};

const parseResponse = async <T>(response: Response): Promise<T> => {
  if (response.status === 401) throw new Error(errorText.AUTH_REQUIRED);
  const body = await response.json().catch(() => null) as ApiEnvelope<T> | null;
  if (!response.ok || !body || !body.success || body.code !== 'OK') throw responseError(response, body);
  return body.data;
};

const fetchAdmin = async (path: string, init: RequestInit, token: string): Promise<Response> => {
  const method = (init.method || 'GET').toUpperCase();
  const headers = new Headers(init.headers);
  if (init.body && !(init.body instanceof FormData) && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
  if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) {
    // A cleared session must not keep sending a stale caller-supplied token.
    headers.delete('X-CSRF-Token');
    if (token) headers.set('X-CSRF-Token', token);
  }
  try {
    // Authenticated APIs must bypass cached permanent redirects as well as data.
    // They never need navigation redirects; reject these without replaying writes.
    return await fetch(`/api/admin${path}`, { ...init, headers, credentials: 'include', cache: 'no-store', redirect: 'error' });
  } catch {
    throw new Error('网络连接失败，请检查后台服务后重试');
  }
};

const requestAdmin = async (path: string, init: RequestInit): Promise<Response> => {
  const sentToken = csrfToken;
  const sentSessionId = csrfSessionId;
  const generation = sessionGeneration;
  const checkSession = (response: Response): Response => {
    // A response from the previous login must not erase or update a newer same-tab session.
    if (generation !== sessionGeneration) throw new Error(errorText.AUTH_REQUIRED);
    if (response.status === 401) return expireSession();
    return response;
  };
  const response = checkSession(await fetchAdmin(path, init, sentToken));
  const method = (init.method || 'GET').toUpperCase();
  const replayable = init.body == null || typeof init.body === 'string' || init.body instanceof FormData;
  if (response.status !== 403 || ['GET', 'HEAD', 'OPTIONS'].includes(method)
      || path === '/auth/login' || !replayable || init.signal?.aborted) return response;
  const error = await response.clone().json().catch(() => null) as ApiEnvelope<unknown> | null;
  if (error?.code !== 'CSRF_TOKEN_INVALID') return response;
  if (generation !== sessionGeneration) throw new Error(errorText.AUTH_REQUIRED);
  if (!sentSessionId || sentSessionId !== csrfSessionId) return expireSession(changedSessionMessage);
  // Only an explicit CSRF rejection is safe to replay: the server has not run the write.
  // Concurrent and late rejections reuse the refreshed token and preserve body/idempotency keys.
  if (csrfToken === sentToken) await refreshCsrf();
  if (generation !== sessionGeneration) throw new Error(errorText.AUTH_REQUIRED);
  if (!csrfSessionId || sentSessionId !== csrfSessionId) return expireSession(changedSessionMessage);
  if (init.signal?.aborted) throw new Error('操作已取消');
  return checkSession(await fetchAdmin(path, init, csrfToken));
};

export const api = async <T>(path: string, init: RequestInit = {}): Promise<T> => {
  const finish = ['GET', 'HEAD'].includes((init.method || 'GET').toUpperCase()) ? () => {} : beginWorkspaceOperation();
  try {
    const generation = sessionGeneration;
    const result = await parseResponse<T>(await requestAdmin(path, init));
    if (generation !== sessionGeneration) throw new Error(errorText.AUTH_REQUIRED);
    return result;
  } finally { finish(); }
};

export const publicApi = async <T>(path: string): Promise<T> => {
  let response: Response;
  try {
    response = await fetch(`/api/public${path}`, {
      method: 'GET',
      credentials: 'omit',
      headers: { Accept: 'application/json' }
    });
  } catch {
    throw new Error('网络连接失败，请检查网络后重试');
  }
  const body = await response.json().catch(() => null) as ApiEnvelope<T> | null;
  if (response.status === 400 || response.status === 404) throw new Error('展示链接无效或已失效');
  if (response.status === 429) throw new Error('访问过于频繁，请稍后重试');
  if (!response.ok || !body || !body.success || body.code !== 'OK') {
    throw new Error('展示数据暂时不可用，请稍后重试');
  }
  return body.data;
};

export const requestSaveLocation = (
  options: SaveLocationOptions = {}
): Promise<SaveLocation | null> => {
  const picker = (window as SaveFilePickerWindow).showSaveFilePicker;
  if (!picker) return Promise.resolve({ fileHandle: null, method: 'browser-download' });

  // Invoke the native picker immediately in the click handler. UI state changes before this call
  // can cause Chromium/macOS to create the chooser without bringing it to the foreground.
  return picker.call(window, {
    suggestedName: options.suggestedName,
    types: options.types,
    excludeAcceptAllOption: false
  }).then((fileHandle) => ({ fileHandle, method: 'file-picker' as const })).catch((error: unknown) => {
    if (error instanceof DOMException && error.name === 'AbortError') return null;
    throw new Error('无法打开保存位置选择窗口，请使用最新版 Chrome 或 Edge 重试');
  });
};

const downloadFile = async (
  path: string,
  init: RequestInit = {},
  options: DownloadOptions = {}
): Promise<DownloadResult | null> => {
  const generation = sessionGeneration;
  const saveLocation = options.saveLocation;
  const fileHandle = saveLocation?.fileHandle ?? null;

  const response = await requestAdmin(path, init);
  if (response.status === 401) throw new Error(errorText.AUTH_REQUIRED);
  if (!response.ok) {
    const body = await response.json().catch(() => null) as ApiEnvelope<unknown> | null;
    throw responseError(response, body);
  }
  const disposition = response.headers.get('Content-Disposition') || '';
  const encoded = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  const fallback = disposition.match(/filename="?([^";]+)"?/i)?.[1];
  const filename = encoded ? decodeURIComponent(encoded) : fallback || '下载文件';
  // Large manufacturing archives go straight to the chosen file instead of a browser-sized Blob.
  if (fileHandle && response.body) {
    if (generation !== sessionGeneration) throw new Error(errorText.AUTH_REQUIRED);
    const reader = response.body.getReader();
    let writable: FileSystemWritableFileStream | undefined;
    let received = 0;
    try {
      writable = await fileHandle.createWritable();
      for (;;) {
        const { done, value } = await reader.read();
        if (generation !== sessionGeneration) throw new Error(errorText.AUTH_REQUIRED);
        if (done) break;
        await writable.write(value);
        received += value.byteLength;
      }
      // Fetch exposes decoded bytes; a compressed transfer's Content-Length is not their length.
      const encoding = response.headers.get('Content-Encoding');
      const expected = !encoding || encoding === 'identity' ? Number(response.headers.get('Content-Length')) : 0;
      if (!received || (expected > 0 && received !== expected)) throw new Error('文件下载不完整，请重试');
      await writable.close();
      return { filename: fileHandle.name || filename, method: 'file-picker' };
    } catch (error) {
      await reader.cancel().catch(() => {});
      try { await writable?.abort(); } catch { /* Preserve the original failure if file cleanup fails. */ }
      if (generation !== sessionGeneration) throw new Error(errorText.AUTH_REQUIRED);
      throw new Error(error instanceof Error && error.message === '文件下载不完整，请重试'
        ? error.message : '文件下载或写入失败，请重新选择保存位置后重试');
    } finally { reader.releaseLock(); }
  }
  // Non-picker browsers cannot stream to disk. Refuse oversized production files before allocating a Blob.
  if (Number(response.headers.get('Content-Length')) > 128 * 1024 * 1024) {
    await response.body?.cancel();
    throw new Error('制作资料较大，请使用电脑 Chrome 或 Edge 浏览器并选择保存位置后下载');
  }
  const file = await response.blob();
  if (generation !== sessionGeneration) throw new Error(errorText.AUTH_REQUIRED);
  if (!file.size) throw new Error('文件内容为空，请稍后重试');

  if (fileHandle) {
    try {
      const writable = await fileHandle.createWritable();
      await writable.write(file);
      await writable.close();
    } catch {
      throw new Error('文件写入失败，请重新选择保存位置');
    }
    return { filename: fileHandle.name || filename, method: 'file-picker' };
  }

  const url = URL.createObjectURL(file);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  // Keep the Blob URL alive long enough for Chromium to hand it to the download manager.
  anchor.click();
  window.setTimeout(() => anchor.remove(), 0);
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  return { filename, method: saveLocation?.method ?? 'browser-download' };
};

export const download: typeof downloadFile = async (...args) => {
  const finish = beginWorkspaceOperation();
  try { return await downloadFile(...args); } finally { finish(); }
};

export const login = async (username: string, password: string) => {
  const session = await api<AdminSession>('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ username, password })
  });
  sessionGeneration += 1;
  csrfRefresh = null;
  csrfToken = session.csrfToken || '';
  csrfSessionId = session.csrfSessionId || '';
  sessionStorage.setItem('yj_admin_csrf', csrfToken);
  sessionStorage.setItem('yj_admin_csrf_session', csrfSessionId);
  return session;
};

export const refreshCsrf = (expectedSessionId?: string): Promise<string> => {
  const generation = sessionGeneration;
  if (csrfRefresh) return csrfRefresh.then((token) => {
    if (generation !== sessionGeneration) throw new Error(errorText.AUTH_REQUIRED);
    if (expectedSessionId && expectedSessionId !== csrfSessionId) return expireSession(changedSessionMessage);
    return token;
  });
  // On page startup, /auth/me is the identity being displayed; its binding takes priority
  // over stale sessionStorage from a previous visit, and is checked before adopting a token.
  const previousSessionId = expectedSessionId || csrfSessionId;
  const pending = (async () => {
    const result = await api<{ csrfToken: string; csrfSessionId?: string }>('/auth/csrf');
    if (generation !== sessionGeneration) throw new Error(errorText.AUTH_REQUIRED);
    if (!result || typeof result.csrfToken !== 'string' || !result.csrfToken.trim()) {
      throw new Error(errorText.CSRF_TOKEN_INVALID);
    }
    const refreshedSessionId = typeof result.csrfSessionId === 'string' ? result.csrfSessionId : '';
    // The cookie may have changed when another tab signed in, even as the same account.
    // Do not adopt its token while this page still holds the previous session's forms.
    if (previousSessionId && previousSessionId !== refreshedSessionId) return expireSession(changedSessionMessage);
    csrfToken = result.csrfToken;
    csrfSessionId = refreshedSessionId;
    sessionStorage.setItem('yj_admin_csrf', csrfToken);
    sessionStorage.setItem('yj_admin_csrf_session', csrfSessionId);
    return csrfToken;
  })();
  csrfRefresh = pending;
  const clearPending = () => { if (csrfRefresh === pending) csrfRefresh = null; };
  void pending.then(clearPending, clearPending);
  return pending;
};

export const logout = async () => {
  await api('/auth/logout', { method: 'POST' });
  sessionGeneration += 1;
  csrfRefresh = null;
  csrfToken = '';
  csrfSessionId = '';
  sessionStorage.removeItem('yj_admin_csrf');
  sessionStorage.removeItem('yj_admin_csrf_session');
};

export const queryString = (values: Record<string, unknown>) => {
  const params = new URLSearchParams();
  Object.entries(values).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
  });
  const value = params.toString();
  return value ? `?${value}` : '';
};
